import React from 'react';
import Link from 'next/link';
import AppLogo from '@/components/ui/AppLogo';
import {
  Phone,
  Mail,
  MapPin,
  MessageCircle,
  ArrowRight,
  Clock,
} from 'lucide-react';

const QUICK_LINKS = [
  { label: 'Home', href: '/' },
  { label: 'Taxi Booking', href: '/taxi-booking' },
  { label: 'Tour Packages', href: '/tour-packages' },
  { label: 'Airport Transfer', href: '/taxi-booking' },
  { label: 'Tempo Traveller', href: '/taxi-booking' },
  { label: 'Track Booking', href: '/booking-status' },
  { label: 'My Dashboard', href: '/dashboard' },
  { label: 'Admin Panel', href: '/admin' },
];

const DESTINATIONS = [
  'Shimla Tour Package',
  'Manali Tour Package',
  'Spiti Valley Tour',
  'Kashmir Tour Package',
  'Leh Ladakh Tour',
  'Uttarakhand Tour',
  'Rajasthan Tour Package',
  'Char Dham Yatra',
];

const POLICIES = [
  { label: 'Privacy Policy', href: '/privacy-policy' },
  { label: 'Terms & Conditions', href: '/terms-and-conditions' },
  { label: 'Cancellation Policy', href: '/cancellation-policy' },
  { label: 'Refund Policy', href: '/refund-policy' },
  { label: 'Payment Policy', href: '/payment-policy' },
  { label: 'Disclaimer', href: '/disclaimer' },
  { label: 'FAQ', href: '/' },
];

export default function Footer() {
  return (
    <footer className="bg-foreground text-white">
      {/* CTA Strip */}
      <div className="bg-primary">
        <div className="max-w-screen-2xl mx-auto px-4 lg:px-8 xl:px-10 py-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div>
              <h3 className="text-xl font-bold text-white">Ready to Explore India?</h3>
              <p className="text-white/80 text-sm mt-1">
                Book your taxi or tour package with Ghumo Ji Travels today.
              </p>
            </div>
            <div className="flex items-center gap-3">
              <a
                href="https://wa.me/918894915859?text=Hi%20Ghumo%20Ji%20Travels%2C%20I%20want%20to%20book%20a%20taxi%2Ftour%20package."
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 bg-[#25D366] text-white font-semibold px-5 py-2.5 rounded-xl hover:bg-[#20b858] transition-all duration-150 text-sm"
              >
                <MessageCircle size={16} />
                WhatsApp Us
              </a>
              <Link
                href="/taxi-booking"
                className="flex items-center gap-2 bg-accent text-accent-foreground font-semibold px-5 py-2.5 rounded-xl hover:bg-yellow-400 transition-all duration-150 text-sm"
              >
                Book Now
                <ArrowRight size={15} />
              </Link>
            </div>
          </div>
        </div>
      </div>
      {/* Main Footer */}
      <div className="max-w-screen-2xl mx-auto px-4 lg:px-8 xl:px-10 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Brand */}
          <div className="lg:col-span-1">
            <div className="flex items-center gap-2 mb-4">
              <AppLogo size={38} />
              <div>
                <div className="font-extrabold text-white text-lg leading-none">Ghumo Ji</div>
                <div className="text-white/60 text-xs">Travels</div>
              </div>
            </div>
            <p className="text-white/65 text-sm leading-relaxed mb-5">
              Your trusted travel partner for taxi bookings, tour packages, and memorable journeys
              across Himachal Pradesh, Kashmir, Rajasthan, and beyond.
            </p>
            <div className="flex flex-col gap-2.5">
              <a
                href="tel:+918894915859"
                className="flex items-center gap-2 text-sm text-white/75 hover:text-accent transition-colors"
              >
                <Phone size={15} className="text-accent flex-shrink-0" />
                +91 88949 15859
              </a>
              <a
                href="mailto:booking@ghumoji.com"
                className="flex items-center gap-2 text-sm text-white/75 hover:text-accent transition-colors"
              >
                <Mail size={15} className="text-accent flex-shrink-0" />
                booking@ghumoji.com
              </a>
              <a
                href="mailto:info@ghumoji.com"
                className="flex items-center gap-2 text-sm text-white/75 hover:text-accent transition-colors"
              >
                <Mail size={15} className="text-accent flex-shrink-0" />
                info@ghumoji.com
              </a>
              <div className="flex items-center gap-2 text-sm text-white/75">
                <Clock size={15} className="text-accent flex-shrink-0" />
                24×7 Customer Support
              </div>
            </div>
            <div className="flex items-center gap-3 mt-5">
              <a
                href="https://facebook.com"
                target="_blank"
                rel="noopener noreferrer"
                className="w-9 h-9 bg-white/10 rounded-lg flex items-center justify-center hover:bg-primary transition-colors"
                aria-label="Facebook"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"/></svg>
              </a>
              <a
                href="https://instagram.com"
                target="_blank"
                rel="noopener noreferrer"
                className="w-9 h-9 bg-white/10 rounded-lg flex items-center justify-center hover:bg-pink-600 transition-colors"
                aria-label="Instagram"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="2" width="20" height="20" rx="5" ry="5"/><path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"/><line x1="17.5" y1="6.5" x2="17.51" y2="6.5"/></svg>
              </a>
              <a
                href="https://youtube.com"
                target="_blank"
                rel="noopener noreferrer"
                className="w-9 h-9 bg-white/10 rounded-lg flex items-center justify-center hover:bg-red-600 transition-colors"
                aria-label="YouTube"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M22.54 6.42a2.78 2.78 0 0 0-1.95-1.96C18.88 4 12 4 12 4s-6.88 0-8.59.46a2.78 2.78 0 0 0-1.95 1.96A29 29 0 0 0 1 12a29 29 0 0 0 .46 5.58A2.78 2.78 0 0 0 3.41 19.6C5.12 20 12 20 12 20s6.88 0 8.59-.46a2.78 2.78 0 0 0 1.95-1.95A29 29 0 0 0 23 12a29 29 0 0 0-.46-5.58z"/><polygon points="9.75 15.02 15.5 12 9.75 8.98 9.75 15.02" fill="white"/></svg>
              </a>
              <a
                href="https://wa.me/918894915859"
                target="_blank"
                rel="noopener noreferrer"
                className="w-9 h-9 bg-white/10 rounded-lg flex items-center justify-center hover:bg-[#25D366] transition-colors"
                aria-label="WhatsApp"
              >
                <MessageCircle size={16} />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-bold text-white text-sm uppercase tracking-wider mb-4">
              Quick Links
            </h4>
            <ul className="flex flex-col gap-2">
              {QUICK_LINKS?.map((link) => (
                <li key={`footer-link-${link?.label}`}>
                  <Link
                    href={link?.href}
                    className="flex items-center gap-2 text-sm text-white/65 hover:text-accent transition-colors group"
                  >
                    <ArrowRight
                      size={13}
                      className="text-accent/50 group-hover:text-accent transition-colors"
                    />
                    {link?.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Destinations */}
          <div>
            <h4 className="font-bold text-white text-sm uppercase tracking-wider mb-4">
              Popular Destinations
            </h4>
            <ul className="flex flex-col gap-2">
              {DESTINATIONS?.map((dest) => (
                <li key={`footer-dest-${dest}`}>
                  <Link
                    href="/tour-packages"
                    className="flex items-center gap-2 text-sm text-white/65 hover:text-accent transition-colors group"
                  >
                    <MapPin
                      size={13}
                      className="text-accent/50 group-hover:text-accent transition-colors flex-shrink-0"
                    />
                    {dest}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Office Addresses + Policies */}
          <div>
            <h4 className="font-bold text-white text-sm uppercase tracking-wider mb-4">
              Our Offices
            </h4>
            <div className="flex flex-col gap-4 mb-6">
              <div className="bg-white/5 rounded-xl p-3 border border-white/10">
                <p className="text-xs font-bold text-accent mb-1">Registered Office</p>
                <p className="text-xs text-white/70 leading-relaxed">
                  Ghumo Ji Travels<br />
                  VPO Udaipur, Tehsil &amp; District Chamba<br />
                  Himachal Pradesh – India
                </p>
              </div>
              <div className="bg-white/5 rounded-xl p-3 border border-white/10">
                <p className="text-xs font-bold text-accent mb-1">Branch Office</p>
                <p className="text-xs text-white/70 leading-relaxed">
                  Ghumo Ji Travels<br />
                  Indora More, VPO Surajpur<br />
                  Tehsil Indora, District Kangra<br />
                  Himachal Pradesh – India
                </p>
              </div>
            </div>

            <h4 className="font-bold text-white text-sm uppercase tracking-wider mb-3">
              Legal &amp; Support
            </h4>
            <ul className="flex flex-col gap-2">
              {POLICIES?.map((policy) => (
                <li key={`footer-policy-${policy?.label}`}>
                  <Link
                    href={policy?.href}
                    className="text-sm text-white/65 hover:text-accent transition-colors"
                  >
                    {policy?.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
      {/* Bottom Bar */}
      <div className="border-t border-white/10">
        <div className="max-w-screen-2xl mx-auto px-4 lg:px-8 xl:px-10 py-4 flex flex-col sm:flex-row items-center justify-between gap-2">
          <p className="text-white/50 text-xs">
            © 2026 Ghumo Ji Travels. All rights reserved. | <a href="https://ghumoji.com" className="hover:text-accent transition-colors">ghumoji.com</a>
          </p>
          <p className="text-white/40 text-xs">
            Explore More. Travel Better.
          </p>
        </div>
      </div>
    </footer>
  );
}